import { useRouter } from "next/router"
import stateCityDictionary, {
  formatName,
  urlifyComplexName,
  urlifyName,
} from "../../../constants/stateCityDictionary"
import PageHead from "../../../components/head/PageHead"
import PageNav from "../../../storyblok/sections/PageNav"
import PetTransportWays from "../../../sections/pet-shipping/PetTransportWays"
import PetTransportLaws from "../../../sections/pet-shipping/PetTransportLaws"
import PetShippingCost from "../../../sections/pet-shipping/PetShippingCost"
import PetTransportCoverage from "../../../sections/pet-shipping/PetTransportCoverage"
import PetShippingBlogLinks from "../../../sections/pet-shipping/PetShippingBlogLinks"
import TopTransporters from "../../../sections/pet-shipping/TopTransporters"
import Breadcrumb from "../../../components/breadcrumbs"
import Footer from "../../../storyblok/static/Footer"
import {
  petShippingStatesJson as petShippingStatesJsonRaw,
  ShippingStates,
} from "../../../constants/pet-shipping/petShippingStates"
import RecentPetTransports from "../../../sections/pet-shipping/RecentPetTransports"
import LinkBlock from "../../../components/link-block"
import { stateShipmentsData } from "../../../constants/pet-shipping/stateShipments"
import { stateCityShipmentsData } from "../../../constants/pet-shipping/stateCityShipments"
import LocalPageHeader from "../../../storyblok/sections/LocalPageHeader"
import stateNeedForTransport from "../../../constants/pet-shipping/state-need-for-transport.json"
import { ReviewsContextProvider } from "../../../context/ReviewsContext"
import stateCityImages from "../../../constants/pet-shipping/state-city-images.json"
import {
  getStateShipmentData,
  getTransportersData,
  getCachedData,
} from "../../../services/cache"
import { GetServerSideProps } from "next"
import { ErrorBoundary } from "../../../components/ErrorBoundary"
import { useEffect, useState } from "react"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import LazyYoutube from "@/components/testimonials/LazyYoutube"

interface DataItem {
  state: string
  data: string
  services: string
}

interface StateShipments {
  state: string
  name: string
  shipmentPosted: string
}

interface CityData {
  state: string
  shipmentPosted: string
  city: string
  cityState: string
}

interface Transporter {
  dba: string
  photo: string
  pickupStateCode: string
  pickupCity: string
  avgRating: string
  userId?: number
  vehicleType?: string
}

interface IPetShippingProps {
  stateData: ShippingStates["description"]
  metaTitle: ShippingStates["metaTitle"]
  metaDescription: ShippingStates["metaDescription"]
  propsHeading: ShippingStates["propsHeading"]
  propsSubheading: ShippingStates["propsSubheading"]
  propsDescription: ShippingStates["propsDescription"]

  linksData: { header: string; link: string; subtitle: string }[]
  citiesLinksData: { header: string; link: string; subtitle: string }[]
  needForTransportData: Record<string, DataItem[]>
  initialShipments: {
    exportShipments: Array<Record<string, any>>
    importShipments: Array<Record<string, any>>
  }
  initialTransporters: Transporter[]
}

const groupByState = (data: DataItem[]): Record<string, DataItem[]> => {
  // First, group the data by state
  const grouped = data.reduce<Record<string, DataItem[]>>((acc, item) => {
    const state = item.state
    if (!acc[state]) {
      acc[state] = []
    }
    acc[state].push(item)
    return acc
  }, {})

  // Then, process the first item in each group to add the image
  Object.values(grouped).forEach((items) => {
    if (items.length > 0 && !items[0].services.includes("home_hero.png")) {
      const defaultHeroImage = `<div class='flex justify-center my-8 max-w-[1189px] mx-auto'><img src='https://a-us.storyblok.com/f/1002522/1122x952/8cd44ec9da/home_hero.png/m/' alt='Pet Transport Services' width='600' height='720' class='rounded-lg shadow-lg' /></div>`
      const h2EndIndex = items[0].services.indexOf("</h2>")
      if (h2EndIndex !== -1) {
        items[0].services =
          items[0].services.slice(0, h2EndIndex + 5) +
          defaultHeroImage +
          items[0].services.slice(h2EndIndex + 5)
      }
    }
  })

  return grouped
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const findHeroImage = (state: string) => {
  const stateSlug = urlifyName(state)

  const image = stateCityImages.find(
    (img) => img.page_type === "state" && img.state_slug === stateSlug,
  )

  return image
    ? {
        url: image.hero_image_url,
        alt: image.hero_image_alt_text,
        width: parseInt(image.hero_image_width),
        height: parseInt(image.hero_image_height),
      }
    : null
}

export default function PetShippingLegacy({
  stateData,
  linksData,
  citiesLinksData,
  needForTransportData,
  initialShipments,
  initialTransporters,
}: IPetShippingProps) {
  const router = useRouter()
  const { state } = router.query
  const formattedState = formatName(state as string)

  const heroImage = findHeroImage(state as string)

  const insertHeroImageAfterH2 = (html: string): string => {
    if (!heroImage) return html

    const maxWidth = 600
    const scaledWidth = Math.min(heroImage.width, maxWidth)
    const scaledHeight = Math.floor(
      (scaledWidth * heroImage.height) / heroImage.width,
    )

    const heroImageHtml = `
      <div class="flex justify-center my-8 max-w-[1189px] mx-auto">
        <img 
          src="${heroImage.url}" 
          alt="${heroImage.alt}"
          width="${scaledWidth}"
          height="${scaledHeight}"
          class="rounded-lg shadow-lg"
        />
      </div>
    `

    // Find the first closing h2 tag and insert the hero image after it
    const h2ClosingTagIndex = html.indexOf("</h2>")
    if (h2ClosingTagIndex === -1) {
      // If no h2 tag found, add one at the beginning
      const defaultTitle = `Discover ${formattedState}`
      return `<h2 class="text-center text-blue7 font-montserrat text-3xl mb-10">${defaultTitle}</h2>${heroImageHtml}${html}`
    }

    return (
      html.slice(0, h2ClosingTagIndex + 5) +
      heroImageHtml +
      html.slice(h2ClosingTagIndex + 5)
    )
  }

  const breadcrumbSchemaJSON = `    {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Pet Transportation",
      "item": "https://citizenshipper.com/pet-transportation"
    },
   {
      "@type": "ListItem",
      "position": 2,
      "name": "All Cities",
      "item": "https://citizenshipper.com/pet-shipping/all-cities"
  },
   {
      "@type": "ListItem",
      "position": 3,
      "name": "${formattedState} Pet Shipping",
      "item": "https://citizenshipper.com/pet-shipping/${state}"
  }
  ]
  }`
  const props = {
    heading: `${stateData.propsHeading}`,
    subheading: `${stateData.propsSubheading}`,
    description: `${stateData.propsDescription}`,
    smBgImage: {
      alt: "Pet Shipping Services",
      filename:
        "https://a-us.storyblok.com/f/1002522/716x860/f51afecb18/header-hero.png",
    },
    hasLongDescription: true,
    buttonLabel: "Get Pet Transportation Quotes",
    buttonLink: "shipments/new/animals",
    image: {
      alt: "Pet Shipping Services",
      filename:
        "https://a-us.storyblok.com/f/1002522/716x860/f51afecb18/header-hero.png",
    },
  }
  const items = [
    { name: "Home", href: "/" },
    { name: "Pets", href: "/pet-transportation" },
    { name: "All Cities", href: "/pet-shipping/all-cities" },
    {
      name: formattedState as string,
      href: `/pet-shipping/${state}`,
    },
  ]
  const stateAbbrev = stateCityDictionary[formattedState]?.abbreviation
  const stateShippingData = needForTransportData[formattedState]
  const snippet = `{
    "@context": "http://schema.org",
    "@type": "VideoObject",
    "name": "Real User Reviews: Why CitizenShipper is the #1 Choice for Pet Shipping!",
    "description": "Thinking about transporting your pet and seeking genuine feedback? Look no further! In this video, real users share their authentic experiences with CitizenShipper - the leading platform for pet transportation. From initial hesitations to unforgettable journeys, these testimonials shine a light on the reliability and excellence of the service.",
    "thumbnailUrl": "https://i3.ytimg.com/vi/d2Ng53qfkqE/maxresdefault.jpg",
    "uploadDate": "2024-03-25T18:11:54Z",
    "duration": "PT1M11S",
    "embedUrl": "https://www.youtube.com/embed/d2Ng53qfkqE"
    }`

  const [shipmentData, setShipmentData] = useState(initialShipments)
  const [topTransporters, setTopTransporters] = useState(initialTransporters)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    cities: false,
    states: false,
  })

  const toggleSection = (section: "cities" | "states") => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleVideoClick = () => {
    setVideoLoaded(true)
  }

  useEffect(() => {
    // Only fetch if we don't have data in either source
    if (
      (!initialShipments?.exportShipments?.length &&
        !shipmentData?.exportShipments?.length) ||
      (!initialShipments?.importShipments?.length &&
        !shipmentData?.importShipments?.length) ||
      (!initialTransporters?.length && !topTransporters?.length)
    ) {
      const fetchData = async () => {
        try {
          const stateAbbrev = stateCityDictionary[
            formattedState
          ]?.abbreviation?.toLowerCase()
          const [shipmentData, transportersData] = await Promise.all([
            getStateShipmentData(stateAbbrev),
            getTransportersData("state", stateAbbrev),
          ])

          setShipmentData(shipmentData)
          setTopTransporters(transportersData)
          // Update initial data to prevent further retries if we got valid data
          if (
            shipmentData?.exportShipments?.length &&
            shipmentData?.importShipments?.length
          ) {
            initialShipments = shipmentData
          }
          if (transportersData?.length) {
            initialTransporters = transportersData
          }
        } catch (error) {
          console.error("Client-side fetch failed:", error)
        }
      }
      fetchData()
    }
  }, [initialShipments, initialTransporters, formattedState])

  return (
    <ReviewsContextProvider>
      <PageNav noBlok buttonLabel="Get Transportation Quotes" />
      <section className="absolute md:relative z-50 top-20 md:pt-22 px-4 lg:px-10 lg:mx-auto lg:max-w-[1300px]">
        <div className="bg-white md:bg-transparent rounded-sm px-2 md:px-0 md:pt-4 md:hidden">
          <Breadcrumb items={items} />
        </div>
      </section>
      <LocalPageHeader {...props} items={items} />
      <PageHead
        title={stateData.metaTitle}
        description={stateData.metaDescription}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: breadcrumbSchemaJSON }}
        />
      </PageHead>

      <section className="relative pt-0 xl:mt-2 py-2 px-4 lg:px-10 lg:mx-auto lg:max-w-[1300px]">
        <ErrorBoundary>
          <RecentPetTransports
            topExportShipments={
              initialShipments?.exportShipments?.length
                ? initialShipments.exportShipments
                : shipmentData?.exportShipments?.length
                ? shipmentData.exportShipments
                : []
            }
            topImportShipments={
              initialShipments?.importShipments?.length
                ? initialShipments.importShipments
                : shipmentData?.importShipments?.length
                ? shipmentData.importShipments
                : []
            }
          />
        </ErrorBoundary>
        <PetTransportLaws needForTransport={needForTransportData} />
        <PetShippingCost />
      </section>
      <PetTransportWays />
      <section className="max-w-[1189px] mx-auto px-2 my-10">
        <PetTransportCoverage />
        <TopTransporters
          topTransporters={
            initialTransporters?.length
              ? initialTransporters
              : topTransporters?.length
              ? topTransporters
              : []
          }
        />
        <div className="py-10 md:py-16 mx-auto md:mx-10 md:px-12">
          <h3 className="text-blue7 text-center mb-4 md:mb-6 leading-10">
            Real User Reviews: Here's Why CitizenShipper is the #1 Choice for
            Pet Shipping!
          </h3>
          <div className="video-container relative">
            {!videoLoaded ? (
              <div
                className="cursor-pointer relative"
                onClick={handleVideoClick}
              >
                <img
                  src="https://dxy7kbcx3zqmq.cloudfront.net/real-user-reviews-d2ng53qfkqe_1744718934.png"
                  alt="Real User Reviews: Why CitizenShipper is the #1 Choice for Pet Shipping!"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-red-600 rounded-full flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="white"
                      className="w-8 h-8 md:w-10 md:h-10 ml-1"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <LazyYoutube
                  videoId="d2Ng53qfkqE"
                  title="Real User Reviews: Why CitizenShipper is the #1 Choice for Pet Shipping!"
                />
              </>
            )}
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: snippet }}
          />
        </div>
        {stateShippingData && (
          <section>
            <div className="prose-headings:font-montserrat prose-h3:text-2xl prose-h2:text-center prose-h2:text-blue7 prose-h3:mt-4 prose-p:text-lg prose-p:m-0 prose-p:py-1.5">
              {safelyRenderHtml(stateShippingData[0]?.services || "")}
            </div>
          </section>
        )}
        <section className="pt:24 prose-h2:font-montserrat prose-h3:font-montserrat prose-h2:text-3xl prose-h2:mb-10 prose-h2:text-blue7 prose-h2:text-center prose-h2:mt-12 prose-p:text-lg prose-h3:mb-6  prose-h3:mt-8 prose-p:mb-4">
          {safelyRenderHtml(insertHeroImageAfterH2(stateData.description))}
        </section>
        <PetShippingBlogLinks />
      </section>
      <section className="mb-8 -mt-8">
        <div className="mx-auto p-4 md:max-w-[600px] lg:max-w-[1380px] text-center">
          <div className="mb-6">
            <button
              onClick={() => toggleSection("cities")}
              className="flex items-center justify-between w-full bg-white p-3 rounded shadow-sm hover:bg-gray-50 transition-colors"
              aria-expanded={expandedSections.cities}
              aria-controls="cities-links"
            >
              <h2 className="text-md font-montserrat m-0 text-left">
                Popular Pet Transport Destinations in {formattedState}
              </h2>
              <svg
                className={`w-5 h-5 transition-transform ${
                  expandedSections.cities ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            <div
              id="cities-links"
              className={`transition-all duration-300 overflow-hidden ${
                expandedSections.cities
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
              role="region"
              aria-labelledby="cities-heading"
            >
              <div className="py-3">
                <LinkBlock
                  blok={{
                    title: "",
                    items: citiesLinksData,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <button
              onClick={() => toggleSection("states")}
              className="flex items-center justify-between w-full bg-white p-3 rounded shadow-sm hover:bg-gray-50 transition-colors"
              aria-expanded={expandedSections.states}
              aria-controls="states-links"
            >
              <h2 className="text-md font-montserrat m-0 text-left">
                All Pet Transport Destination States Across the US
              </h2>
              <svg
                className={`w-5 h-5 transition-transform ${
                  expandedSections.states ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            <div
              id="states-links"
              className={`transition-all duration-300 overflow-hidden ${
                expandedSections.states
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
              role="region"
              aria-labelledby="states-heading"
            >
              <div className="py-3">
                <LinkBlock
                  blok={{
                    title: "",
                    items: linksData,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer noBlok />
    </ReviewsContextProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const { state } = params as { state: string }
  const formattedState = formatName(state)

  // Validate state exists
  if (!stateCityDictionary[formattedState]) {
    return { notFound: true }
  }

  try {
    const pageData = await getCachedData(`page:static:${state}`, async () => {
      const formattedState = formatName(state)

      const stateData = petShippingStatesJsonRaw[state as keyof ShippingStates]

      return {
        stateData: {
          metaTitle: stateData?.metaTitle || "",
          metaDescription: stateData?.metaDescription || "",
          propsHeading: stateData?.propsHeading || "",
          propsSubheading: stateData?.propsSubheading || "",
          propsDescription: stateData?.propsDescription || "",
          description: stateData?.description || "",
        },
        linksData: getStateLinks(stateShipmentsData, state),
        citiesLinksData: getStateCities(stateCityShipmentsData, state),
      }
    })

    const weeklyData = await getCachedData(`page:weekly:${state}`, async () => {
      const formattedState = formatName(state)

      const groupedData = groupByState(stateNeedForTransport)
      return {
        needForTransportData: {
          [formattedState]: stateNeedForTransport.filter(
            (item) => item.state.toLowerCase() === state.toLowerCase(),
          ),
        },
      }
    })

    const frequentData = await getCachedData(
      `page:frequent:${state}`,
      async () => {
        const formattedState = formatName(state)
        const stateAbbrev = stateCityDictionary[
          formattedState
        ]?.abbreviation?.toLowerCase()

        try {
          const [shipmentData, transportersData] = await Promise.all([
            getStateShipmentData(stateAbbrev),
            getTransportersData("state", stateAbbrev),
          ])

          return {
            initialShipments: shipmentData,
            initialTransporters: transportersData || [],
          }
        } catch (error: any) {
          console.error("ShipmentService call failed for state:", {
            state,
            error: error.message,
          })
          return {
            initialShipments: {
              exportShipments: [],
              importShipments: [],
            },
            initialTransporters: [],
          }
        }
      },
    )

    return {
      props: {
        ...pageData,
        ...weeklyData,
        ...frequentData,
      },
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("not found")) {
      return { notFound: true }
    }

    return {
      props: {
        stateData: {
          metaTitle: "",
          metaDescription: "",
          propsHeading: `Pet Transport Services in ${formatName(state)}`,
          propsSubheading: "Safe and Reliable Pet Transportation",
          propsDescription: `Looking for pet shipping services in ${formatName(
            state,
          )}? Get free quotes from verified transporters.`,
          description: "",
        },
        linksData: [],
        citiesLinksData: [],
        needForTransportData: {},
        initialShipments: { exportShipments: [], importShipments: [] },
        initialTransporters: [],
      },
    }
  }
}

function getStateLinks(obj: StateShipments[], state: string) {
  return obj
    .filter((item) => item.state !== state)
    .map((key) => ({
      header: key.name,
      subtitle: `${key.shipmentPosted} Shipments`,
      link: `/pet-shipping/${urlifyName(key.name)}`,
    }))
}

function getStateCities(obj: CityData[], state: string) {
  return obj
    .filter((item) => item.state === formatName(state))
    .map((item) => ({
      header: item.cityState,
      subtitle: `${item.shipmentPosted} Shipments`,
      link: `/pet-shipping/${urlifyName(item.state)}/${urlifyName(item.city)}`,
    }))
}

function getStateNeedForTransport(
  data: Record<string, DataItem[]>,
  state: string,
): DataItem[] {
  return data[state] || []
}
